import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import apiClient from '../../utils/api';

const EMPTY = { name: '', registrationNumber: '', email: '', phone: '', address: '', city: '', country: 'Rwanda', currency: 'RWF', allowApiIntegration: false, allowWebhooks: false };

export const SuperAdminCompanyForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [form, setForm] = useState({ ...EMPTY });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEdit) {
      apiClient.getCompany(id!).then(r => {
        const d = r.data.data;
        setForm({ name: d.name, registrationNumber: d.registrationNumber ?? '', email: d.email ?? '', phone: d.phone ?? '', address: d.address ?? '', city: d.city ?? '', country: d.country, currency: d.currency, allowApiIntegration: d.allowApiIntegration, allowWebhooks: d.allowWebhooks });
      }).catch(() => {});
    }
  }, [id, isEdit]);

  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      if (isEdit) { await apiClient.updateCompany(id!, form); navigate(`/super-admin/companies/${id}`); }
      else { const r = await apiClient.createCompany(form); navigate(`/super-admin/companies/${r.data.data?.id ?? r.data.data?.company?.id}`); }
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Failed to save company');
    } finally { setLoading(false); }
  };

  return (
    <>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-ghost" onClick={() => navigate(-1)}><ArrowLeft size={16} /></button>
          <h1 className="page-title">{isEdit ? 'Edit Company' : 'New Company'}</h1>
        </div>
      </div>

      <div className="card" style={{ maxWidth: 700 }}>
        {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 14 }}>{error}</div>}
        <form onSubmit={submit}>
          <div className="form-grid">
            <div className="form-group">
              <label>Company Name *</label>
              <input required value={form.name} onChange={e => set('name', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Registration Number</label>
              <input value={form.registrationNumber} onChange={e => set('registrationNumber', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" value={form.email} onChange={e => set('email', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input value={form.phone} onChange={e => set('phone', e.target.value)} />
            </div>
            <div className="form-group full">
              <label>Address</label>
              <input value={form.address} onChange={e => set('address', e.target.value)} />
            </div>
            <div className="form-group">
              <label>City</label>
              <input value={form.city} onChange={e => set('city', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Country *</label>
              <input required value={form.country} onChange={e => set('country', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Currency *</label>
              <select value={form.currency} onChange={e => set('currency', e.target.value)}>
                {['RWF', 'USD', 'EUR', 'KES', 'UGX', 'TZS'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.allowApiIntegration} onChange={e => set('allowApiIntegration', e.target.checked)} />
                Allow API Integration
              </label>
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.allowWebhooks} onChange={e => set('allowWebhooks', e.target.checked)} />
                Allow Webhooks
              </label>
            </div>
          </div>
          <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              <Save size={15} /> {loading ? 'Saving...' : 'Save Company'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => navigate(-1)}>Cancel</button>
          </div>
        </form>
      </div>
    </>
  );
};
